import type { EmailPreview, ParsedAIEmail } from './types';

type ExtractionResult<T = unknown> = {
  /** The original string with the first JSON object removed (surrounding blank lines tidied). */
  cleaned: string;
  /** The parsed JSON object, or null if none found / not parseable. */
  json: T | null;
  /** The index in the original string where the JSON object started, or -1 if none found. */
  index: number;
};

const EMAIL_PREVIEWS_PREFIX = '```json_email_previews';
const EMAIL_PREFIX = '```json_email';
const EMPTY_EMAIL: Readonly<ParsedAIEmail> = Object.freeze({ subject: '', body: '' });
const CHART_OBJECT_START_RE = /^\{\s*"chart"\s*:/;
const PREVIEW_OBJECT_START_RE = /^\{\s*"subject"\s*:/;

const createParseJSONService = () => {
  const stripFenceArtifacts = (chunk: string): string => {
    if (!chunk) {
      return '';
    }
    return chunk.replace(/`{3,}/gi, '');
  };

  /**
   * Finds the closing quote of a JSON string value.
   * @param s - The source string
   * @param from - The index to start searching from (after the opening quote)
   * @param trackBraces - If true, tracks brace depth to handle embedded JSON with unescaped quotes.
   *                      Only considers a quote as "closing" when brace depth is 0.
   */
  const findClosingQuote = (s: string, from: number, trackBraces = false): number => {
    let escaped = false;
    let braceDepth = 0;

    for (let i = from; i < s.length; i++) {
      const ch = s[i];
      if (escaped) {
        escaped = false;
        continue;
      }
      if (ch === '\\') {
        escaped = true;
        continue;
      }

      if (trackBraces) {
        if (ch === '{') {
          braceDepth++;
          continue;
        }
        if (ch === '}') {
          braceDepth--;
          continue;
        }
      }

      if (ch === '"' && braceDepth === 0) {
        return i;
      }
    }
    return -1;
  };

  const extractField = (source: string, fieldName: 'subject' | 'body'): string => {
    const keyRe = new RegExp(`"${fieldName}"\\s*:\\s*"`, 'g');
    const m = keyRe.exec(source);
    if (!m) {
      return '';
    }
    const start = m.index + m[0].length;
    // Track braces for body field to handle embedded JSON with unescaped quotes
    const trackBraces = fieldName === 'body';
    const end = findClosingQuote(source, start, trackBraces);
    return end === -1 ? source.slice(start) : source.slice(start, end);
  };

  const decodeBasic = (raw: string): string => {
    if (!raw) {
      return '';
    }
    return raw
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '\r')
      .replace(/\\t/g, '\t')
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\');
  };

  const isLikeEmailJSON = (input: string): boolean => {
    if (typeof input !== 'string') {
      return false;
    }
    const trimmed = input.trim();

    if (trimmed.startsWith(EMAIL_PREFIX)) {
      return true;
    }

    if (!trimmed.startsWith('{')) {
      return false;
    }
    if (trimmed.indexOf('"subject"') !== -1 && trimmed.indexOf('"body"') !== -1 && trimmed.indexOf('"chart"') !== -1) {
      return true;
    }

    return false;
  };

  const parseEmail = (input: string): ParsedAIEmail => {
    if (!isLikeEmailJSON(input)) {
      return EMPTY_EMAIL;
    }

    const afterPrefix = input.startsWith(EMAIL_PREFIX) ? input.slice(EMAIL_PREFIX.length) : input;
    const stripped = stripFenceArtifacts(afterPrefix).trim();

    const subject = decodeBasic(extractField(stripped, 'subject')).trim();
    const body = decodeBasic(extractField(stripped, 'body')).trim();

    const email: ParsedAIEmail = { subject, body };
    return email;
  };

  const isEscaped = (s: string, idx: number): boolean => {
    let backslashes = 0;
    for (let k = idx - 1; k >= 0 && s[k] === '\\'; k--) {
      backslashes++;
    }
    return backslashes % 2 === 1;
  };

  const isChartObjectStart = (s: string, start: number): boolean => CHART_OBJECT_START_RE.test(s.slice(start));
  const isPreviewObjectStart = (s: string, start: number): boolean => PREVIEW_OBJECT_START_RE.test(s.slice(start));

  const findJSONObjectRange = (
    s: string,
    startCheck: (s: string, pos: number) => boolean = isChartObjectStart
  ): { start: number; end: number } | null => {
    for (let i = 0; i < s.length; i++) {
      if (s[i] !== '{') {
        continue;
      }
      if (!startCheck(s, i)) {
        continue;
      }

      let depth = 0;
      let inString = false;
      let end = -1;

      for (let j = i; j < s.length; j++) {
        const c = s[j];

        if (inString) {
          if (c === '"' && !isEscaped(s, j)) {
            inString = false;
          }
          continue;
        }
        if (c === '"') {
          inString = true;
          continue;
        }

        if (c === '{') {
          depth++;
        } else if (c === '}') {
          depth--;
          if (depth === 0) {
            end = j;
            break;
          }
        }
      }

      if (end !== -1) {
        return { start: i, end };
      }
    }
    return null;
  };

  const extractEmbeddedJSON = <T = unknown>(input: string): ExtractionResult<T> => {
    const range = findJSONObjectRange(input);
    if (!range) {
      return { cleaned: input, json: null, index: -1 };
    }

    const { start, end } = range;
    const jsonText = input.slice(start, end + 1);

    let parsed: T | null = null;
    try {
      parsed = JSON.parse(jsonText) as T;
    } catch {
      return { cleaned: input, json: null, index: -1 };
    }

    const before = input.slice(0, start);
    const after = input.slice(end + 1);
    const cleaned = before + after;

    const indexInCleaned = before.length;

    return { cleaned, json: parsed, index: indexInCleaned };
  };

  const isLikeEmailPreviewJSON = (input: string): boolean => {
    return typeof input === 'string' && input.trim().startsWith(EMAIL_PREVIEWS_PREFIX);
  };

  const parseAIEmailPreviews = (input: string): EmailPreview[] => {
    if (!isLikeEmailPreviewJSON(input)) {
      return [];
    }

    // 1) Remove the fixed prefix and strip any remaining fence artifacts (e.g., trailing ```).
    const afterPrefix = input.slice(EMAIL_PREVIEWS_PREFIX.length);
    const stripped = stripFenceArtifacts(afterPrefix).trim();

    // 2) Bound the payload to the JSON array block.
    const start = stripped.indexOf('[');
    const end = stripped.lastIndexOf(']');
    if (start === -1 || end === -1 || end <= start) {
      return [];
    }

    // We'll scan this slice, extracting one JSON object { ... } at a time.
    let rest = stripped.slice(start, end + 1);

    const previews: EmailPreview[] = [];

    // 3) Iterate object-by-object using the service's JSON object range finder.
    while (true) {
      const range = findJSONObjectRange(rest, isPreviewObjectStart);
      if (!range) {
        break;
      }

      const objText = rest.slice(range.start, range.end + 1);

      // 4) Extract fields within THIS object using the service's quote-safe extractor.
      const rawSubject = extractField(objText, 'subject');
      const rawBody = extractField(objText, 'body');

      // 5) Decode escaped sequences consistently with the rest of the service.
      const subject = decodeBasic(rawSubject ?? '');
      const bodyRaw = decodeBasic(rawBody ?? '');

      // 6) Pull out embedded chart JSON (if any) and its anchor index in the cleaned body.
      const { cleaned, json, index } = extractEmbeddedJSON<Record<string, unknown>>(bodyRaw);

      previews.push({
        subject,
        bodyBefore: (index >= 0 ? cleaned.slice(0, index) : cleaned).trim(),
        bodyAfter: (index >= 0 ? cleaned.slice(index) : '').trim(),
        chartJson: json,
      });

      // 7) Advance past this object and continue scanning for the next one.
      rest = rest.slice(range.end + 1);
    }

    return previews;
  };

  const parseEmailPreview = (rawEmail: string): EmailPreview | null => {
    const parsedEmail = parseEmail(rawEmail);
    if (!parsedEmail.subject && !parsedEmail.body) {
      return null;
    }

    const { cleaned, json, index } = extractEmbeddedJSON<Record<string, unknown>>(parsedEmail.body);
    return {
      subject: parsedEmail.subject,
      bodyBefore: (index >= 0 ? cleaned.slice(0, index) : cleaned).trim(),
      bodyAfter: (index >= 0 ? cleaned.slice(index) : '').trim(),
      chartJson: json,
    };
  };

  return {
    parseEmail,
    extractEmbeddedJSON,
    parseAIEmailPreviews,
    parseEmailPreview,
  };
};

const ParseJSONService = createParseJSONService();

export default ParseJSONService;
